// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import type { ICommunicationResponse, ICommunicationRequest } from "shared/types/linxapi";
import { ComInterface } from "./cominterface";

// WebSocket endpoint of the live-data proxy (cloud build). The proxy relays
// raw JSON /DA payloads to/from the controller; all binary framing happens
// proxy<->controller, so the browser just sends/receives JSON.
// This is the browser-facing host of the proxy: it must not request a TLS client
// certificate, as Chromium aborts a WebSocket handshake that does. Controllers
// use the mTLS host enl-tun.embyt.com, which resolves to the same server.
const PROXY_ENDPOINT = import.meta.env.VITE_PROXY_ENDPOINT ?? "wss://enl-tun-client.embyt.com";

const CONNECT_TIMEOUT_MS = 10000;
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;

interface IPendingRequest {
  resolve: (response: ICommunicationResponse) => void;
  reject: (error: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

// Proxy transport to the controller /DA endpoint, used by the AWS frontend
// when the controller is reachable through the WebSocket proxy. Concurrent
// requests are correlated via a unique Options.ClientRequestHandle which the
// controller echoes back in response.Request.
export class ProxyInterface extends ComInterface {
  private serial = "";
  private token = "";
  private ws: WebSocket | undefined;
  private connectPromise: Promise<void> | undefined;
  // rejects a connect that is still in flight, so close() cannot leave it pending
  private abortConnect: ((error: Error) => void) | undefined;
  private readonly pending = new Map<string, IPendingRequest>();
  private seqCounter = 0;
  private intentionalClose = false;
  // invoked when an established connection drops unexpectedly (for fallback)
  public onConnectionLost: (() => void) | undefined;

  public setConnection(serial: string, token: string): void {
    // reconnect if the target changed
    if (serial !== this.serial || token !== this.token) {
      this.serial = serial;
      this.token = token;
      this.close();
    }
  }

  public get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  public connect(): Promise<void> {
    if (this.isConnected) {
      return Promise.resolve();
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }
    let rejectConnect: ((error: Error) => void) | undefined;
    this.connectPromise = new Promise<void>((resolve, reject) => {
      rejectConnect = reject;
      this.abortConnect = reject;
      const url = `${PROXY_ENDPOINT}/client?serial=${encodeURIComponent(
        this.serial,
      )}&token=${encodeURIComponent(this.token)}`;
      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      this.ws = ws;

      const connectTimer = setTimeout(() => {
        reject(new Error("proxy connect timeout"));
        ws.close();
      }, CONNECT_TIMEOUT_MS);

      ws.onopen = () => {
        clearTimeout(connectTimer);
        resolve();
      };
      ws.onerror = () => {
        clearTimeout(connectTimer);
        reject(new Error("proxy connection error"));
      };
      ws.onclose = () => {
        clearTimeout(connectTimer);
        this.handleClose();
        reject(new Error("proxy connection closed"));
        if (!this.intentionalClose) {
          this.onConnectionLost?.();
        }
      };
      ws.onmessage = (event) => this.handleMessage(event);
    }).finally(() => {
      // keep a newer connect, which a close() in between may have started
      if (this.abortConnect === rejectConnect) {
        this.connectPromise = undefined;
        this.abortConnect = undefined;
      }
    });
    return this.connectPromise;
  }

  public close(): void {
    this.intentionalClose = true;
    // detaching the handlers below would leave a pending connect unsettled, so
    // drop it here; connect() must be free to start over right afterwards
    const abortConnect = this.abortConnect;
    this.connectPromise = undefined;
    this.abortConnect = undefined;
    abortConnect?.(new Error("proxy connection closed"));
    const ws = this.ws;
    this.ws = undefined;
    if (ws) {
      ws.onopen = ws.onerror = ws.onclose = ws.onmessage = null;
      ws.close();
    }
    this.handleClose();
    this.intentionalClose = false;
  }

  private handleClose(): void {
    // fail all in-flight requests
    for (const [, req] of this.pending) {
      clearTimeout(req.timer);
      req.reject(new Error("proxy connection closed"));
    }
    this.pending.clear();
  }

  private handleMessage(event: MessageEvent): void {
    let response: ICommunicationResponse;
    try {
      const text =
        typeof event.data === "string" ?
          event.data
        : new TextDecoder().decode(event.data as ArrayBuffer);
      response = JSON.parse(text) as ICommunicationResponse;
    } catch (error) {
      console.warn("cannot parse proxy response", error);
      return;
    }
    const handle = response.Request?.Options?.ClientRequestHandle;
    const key = typeof handle === "string" ? handle : undefined;
    const req = key !== undefined ? this.pending.get(key) : undefined;
    if (!req || key === undefined) {
      console.warn("got proxy response for unknown request", handle);
      return;
    }
    this.pending.delete(key);
    clearTimeout(req.timer);
    req.resolve(response);
  }

  protected async doRequest(body: ICommunicationRequest): Promise<ICommunicationResponse> {
    await this.connect();
    // inject a unique correlation handle (echoed back in response.Request)
    const key = `req-${this.seqCounter++}`;
    const request: ICommunicationRequest = {
      ...body,
      Options: { ...body.Options, ClientRequestHandle: key },
    };

    return new Promise<ICommunicationResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(key);
        reject(new Error("proxy request timeout"));
      }, this.timeout ?? DEFAULT_REQUEST_TIMEOUT_MS);
      this.pending.set(key, { resolve, reject, timer });
      try {
        this.ws!.send(JSON.stringify(request));
      } catch (error) {
        this.pending.delete(key);
        clearTimeout(timer);
        reject(error);
      }
    });
  }
}
